import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiClient } from '@kss/api';
import { Alert, Button, Card, useToast } from '@kss/ui';
import { RefreshCw, CalendarClock, Users, Phone, MapPin, CalendarDays } from 'lucide-react';

/**
 * Table reservations board — replaces the generic API-browser grid.
 *
 * One card per reservation, showing the reserved date/time prominently
 * alongside the customer name, party size, table, phone and any special
 * request. An admin can change the reservation status inline. Filter by
 * Upcoming / All / Past.
 */

const POLL_MS = 30_000;
const RES_STATUSES = ['Pending', 'Confirmed', 'Seated', 'Completed', 'Cancelled', 'NoShow'] as const;

const pickList = (d: unknown): Array<Record<string, unknown>> => {
  if (Array.isArray(d)) return d as Array<Record<string, unknown>>;
  const o = d as { items?: unknown[]; data?: unknown[] | { items?: unknown[] } } | null;
  if (Array.isArray(o?.items)) return o!.items as Array<Record<string, unknown>>;
  if (Array.isArray(o?.data)) return o!.data as Array<Record<string, unknown>>;
  if (o?.data && Array.isArray((o.data as { items?: unknown[] }).items)) return (o.data as { items: unknown[] }).items as Array<Record<string, unknown>>;
  return [];
};

function resColor(s: string): { bg: string; fg: string } {
  switch (s) {
    case 'Confirmed': return { bg: '#dbeafe', fg: '#1d4ed8' };
    case 'Seated': return { bg: '#fde68a', fg: '#9a3412' };
    case 'Completed': return { bg: '#dcfce7', fg: '#166534' };
    case 'Cancelled': case 'NoShow': return { bg: '#fee2e2', fg: '#991b1b' };
    default: return { bg: '#e4e4e7', fg: '#3f3f46' };
  }
}

interface Reservation {
  id: string;
  code: string;
  customerName: string;
  customerPhone?: string;
  tableLabel?: string;
  partySize: number;
  reservedAt?: string;
  durationMinutes?: number;
  status: string;
  specialRequests?: string;
}

type Filter = 'upcoming' | 'all' | 'past';

export default function TableReservations() {
  const toast = useToast();
  const [rows, setRows] = useState<Array<Record<string, unknown>>>([]);
  const [tableMap, setTableMap] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('upcoming');

  const refresh = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const [resRes, tableRes] = await Promise.all([
        apiClient.get('/restaurant/TableReservation'),
        apiClient.get('/restaurant/RestaurantTable').catch(() => ({ data: [] })),
      ]);
      const tMap = new Map<string, string>();
      for (const t of pickList(tableRes.data)) {
        const id = String(t.id ?? (t as { restaurantTableId?: unknown }).restaurantTableId ?? '');
        if (id) tMap.set(id, String(t.name ?? t.code ?? `Table ${id}`));
      }
      setTableMap(tMap);
      setRows(pickList(resRes.data));
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to load reservations.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    const t = window.setInterval(() => void refresh(), POLL_MS);
    return () => window.clearInterval(t);
  }, [refresh]);

  const reservations = useMemo<Reservation[]>(() => rows
    .filter((r) => !String(r.code ?? '').startsWith('SAMPLE-'))
    .map((r) => {
      const tableId = r.tableId != null && String(r.tableId) !== '0' ? String(r.tableId) : undefined;
      const at = r.reservedAt && String(r.reservedAt) !== '0001-01-01T00:00:00' ? String(r.reservedAt) : undefined;
      return {
        id: String(r.id ?? ''),
        code: String(r.code ?? `#${r.id ?? ''}`),
        customerName: String(r.customerName || 'Guest'),
        customerPhone: r.customerPhone ? String(r.customerPhone) : undefined,
        tableLabel: tableId ? (tableMap.get(tableId) ?? `Table ${tableId}`) : undefined,
        partySize: Number(r.partySize ?? 0) || 0,
        reservedAt: at,
        durationMinutes: r.durationMinutes != null ? Number(r.durationMinutes) : undefined,
        status: String(r.reservationStatus ?? 'Pending'),
        specialRequests: r.specialRequests ? String(r.specialRequests) : undefined,
      };
    }), [rows, tableMap]);

  const filtered = useMemo(() => {
    const now = Date.now();
    const list = reservations.filter((r) => {
      if (filter === 'all') return true;
      const t = r.reservedAt ? Date.parse(r.reservedAt) : NaN;
      if (Number.isNaN(t)) return filter === 'upcoming'; // undated → treat as upcoming/active
      return filter === 'upcoming' ? t >= now - 2 * 3600_000 : t < now;
    });
    return list.sort((a, b) => {
      const ta = a.reservedAt ? Date.parse(a.reservedAt) : 0;
      const tb = b.reservedAt ? Date.parse(b.reservedAt) : 0;
      return filter === 'past' ? tb - ta : ta - tb;
    });
  }, [reservations, filter]);

  async function updateStatus(r: Reservation, value: string) {
    setRows((prev) => prev.map((x) => (String(x.id) === r.id ? { ...x, reservationStatus: value } : x)));
    try {
      await apiClient.put('/restaurant/TableReservation', { id: Number(r.id), code: r.code, status: 0, reservationStatus: value });
      toast.success('Reservation updated', `${r.customerName} → ${value}`);
    } catch (e) {
      toast.error('Update failed', e instanceof Error ? e.message : 'Try again');
      void refresh();
    }
  }

  return (
    <div className="-m-6 flex h-[calc(100vh-3.5rem)] flex-col">
      <header className="flex flex-wrap items-center gap-3 border-b border-border bg-surface-card px-4 py-3">
        <CalendarClock className="text-orange-500" size={22} />
        <div className="min-w-0 flex-1">
          <h1 className="text-base font-semibold text-content-primary">Table Reservations</h1>
          <p className="text-[11px] text-content-muted">{filtered.length} reservation(s)</p>
        </div>
        <div className="inline-flex overflow-hidden rounded-md border border-border">
          {(['upcoming', 'all', 'past'] as const).map((f) => (
            <button key={f} onClick={() => setFilter(f)} className={`px-2.5 py-1 text-xs capitalize ${filter === f ? 'bg-primary text-white' : 'bg-surface-card text-content-secondary'}`}>{f}</button>
          ))}
        </div>
        <Button variant="outline" size="sm" onClick={() => void refresh()}>
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </header>

      {err && <Alert variant="danger" className="m-3">{err}</Alert>}

      <div className="flex-1 overflow-y-auto p-3">
        {loading && rows.length === 0 ? (
          <div className="grid place-items-center py-20 text-sm text-content-muted">Loading reservations…</div>
        ) : filtered.length === 0 ? (
          <div className="grid place-items-center py-20 text-center text-sm text-content-muted">
            <CalendarDays className="mb-2 text-content-muted" size={28} />
            No {filter === 'all' ? '' : filter + ' '}reservations.
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filtered.map((r) => {
              const c = resColor(r.status);
              const dt = r.reservedAt ? new Date(r.reservedAt) : null;
              return (
                <Card key={r.id} padded={false} className="flex flex-col gap-2 p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="truncate text-[15px] font-semibold text-content-primary">{r.customerName}</div>
                      {r.customerPhone && <div className="inline-flex items-center gap-1 text-[11px] text-content-muted"><Phone size={11} />{r.customerPhone}</div>}
                    </div>
                    <span className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium" style={{ background: c.bg, color: c.fg }}>{r.status}</span>
                  </div>

                  <div className="rounded-md border border-border bg-surface-app px-2.5 py-2">
                    <div className="inline-flex items-center gap-1.5 text-[13px] font-medium text-content-primary">
                      <CalendarClock size={13} className="text-primary" />
                      {dt ? dt.toLocaleString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'No date set'}
                    </div>
                    {r.durationMinutes ? <div className="mt-0.5 text-[10px] text-content-muted">for {r.durationMinutes} min</div> : null}
                  </div>

                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-content-muted">
                    <span className="inline-flex items-center gap-1"><Users size={11} />{r.partySize || '—'} guest(s)</span>
                    {r.tableLabel && <span className="inline-flex items-center gap-1"><MapPin size={11} />{r.tableLabel}</span>}
                  </div>

                  {r.specialRequests && (
                    <div className="rounded-md bg-orange-50 px-2 py-1 text-[11px] text-orange-800">“{r.specialRequests}”</div>
                  )}

                  <select
                    value={RES_STATUSES.includes(r.status as typeof RES_STATUSES[number]) ? r.status : 'Pending'}
                    onChange={(e) => updateStatus(r, e.target.value)}
                    className="mt-auto h-8 rounded-md border border-border bg-surface-card px-2 text-xs text-content-primary outline-none focus:border-primary"
                    title="Change reservation status"
                  >
                    {RES_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
